import { Component, type ReactNode } from 'react'
import { Coffee, Home, RefreshCcw, Sparkles } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  isChunkError: boolean
}

/**
 * Top-level error boundary — last line of defence above the router.
 *
 * Most production errors that bubble this high are chunk-loading
 * failures from a fresh deploy. We detect that and reload the page on
 * a soft press, instead of leaving the user on a blank screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, isChunkError: false }

  static getDerivedStateFromError(error: Error): State {
    const msg = (error?.message ?? '').toLowerCase()
    const isChunkError =
      msg.includes('chunk') ||
      msg.includes('failed to fetch dynamically imported module') ||
      msg.includes('importing a module script failed') ||
      msg.includes('loading css chunk') ||
      msg.includes('loading chunk')
    return { hasError: true, error, isChunkError }
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // Keep this minimal until Sentry is wired up.
    console.error('[ErrorBoundary]', error, info)
  }

  reset = () => {
    if (this.state.isChunkError) {
      window.location.reload()
      return
    }
    this.setState({ hasError: false, error: null, isChunkError: false })
  }

  render() {
    if (!this.state.hasError) return this.props.children

    const { isChunkError } = this.state
    const isDev = import.meta.env.DEV

    return (
      <div className="relative isolate grid min-h-dvh place-items-center overflow-hidden bg-background p-6 text-foreground">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10"
        >
          <div className="absolute left-1/2 top-1/3 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-gradient-to-br from-primary/30 via-primary/10 to-transparent opacity-40 blur-3xl" />
        </div>

        <div className="max-w-md text-center">
          <div className="relative mx-auto h-20 w-20">
            <span
              aria-hidden
              className="absolute inset-0 animate-pulse rounded-3xl bg-primary/15 blur-xl"
            />
            <span
              aria-hidden
              className="relative grid h-20 w-20 place-items-center rounded-3xl bg-gradient-to-br from-primary/15 to-primary/8 text-primary ring-1 ring-primary/30"
            >
              {isChunkError ? (
                <Sparkles className="h-9 w-9" />
              ) : (
                <Coffee className="h-9 w-9" />
              )}
            </span>
          </div>

          <h1
            className="mt-6 font-display text-2xl font-extrabold tracking-tight sm:text-3xl"
            lang="ar"
            dir="rtl"
          >
            {isChunkError ? 'تحديث جديد للموقع 🎉' : 'حصل اللي مش مفروض يحصل'}
          </h1>
          <p
            className="mx-auto mt-3 text-sm leading-relaxed text-muted-foreground"
            lang="ar"
            dir="rtl"
          >
            {isChunkError
              ? 'كان فيه نسخة جديدة. جدّد الصفحة وكل حاجة هتمشي صح.'
              : 'حصل خطأ غير متوقع. جرّب تجدّد الصفحة. لو فضلت، احكيلنا.'}
          </p>

          <div className="mt-6 flex flex-col items-center justify-center gap-2 sm:flex-row">
            <button
              type="button"
              onClick={this.reset}
              className="inline-flex h-12 items-center gap-2 rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <RefreshCcw className="h-4 w-4" aria-hidden />
              <span lang="ar">{isChunkError ? 'تحديث' : 'حاول تاني'}</span>
            </button>
            <a
              href="/"
              className="inline-flex h-12 items-center gap-2 rounded-xl border border-border bg-card/60 px-6 text-sm font-medium text-foreground/90 hover:border-primary/40 hover:text-foreground"
            >
              <Home className="h-4 w-4" aria-hidden />
              <span lang="ar">الرئيسية</span>
            </a>
          </div>

          {isDev && this.state.error?.message && (
            <pre
              dir="ltr"
              className="mx-auto mt-8 max-h-40 max-w-full overflow-auto rounded-lg border border-border bg-card p-3 text-start text-[11px] text-muted-foreground"
            >
              {this.state.error.message}
            </pre>
          )}
        </div>
      </div>
    )
  }
}
